import { useSnackbar } from "notistack";
import React, { useState, useEffect, useRef } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import Spinner from "../components/Spinner";
import { extractReferenceFaceDescriptor } from "../utils/faceRecognition";
import { FaRandom, FaCopy, FaWhatsapp } from "react-icons/fa";

const EditAtleta = () => {
  const [nome, setNome] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [chaveAcesso, setChaveAcesso] = useState("");

  const [celular, setCelular] = useState("");
  const [perfil, setPerfil] = useState("");
  const [loading, setLoading] = useState(false);

  // Fotos de referência para reconhecimento facial
  const [fotosReferencia, setFotosReferencia] = useState([]);
  const [uploadingFace, setUploadingFace] = useState(false);
  const [faceStatusText, setFaceStatusText] = useState("");
  const faceInputRef = useRef(null);

  // Atletas representados
  const [atletasList, setAtletasList] = useState([]);
  const [selectedRepresentados, setSelectedRepresentados] = useState([]);
  const [searchAtletaTerm, setSearchAtletaTerm] = useState("");

  const navigate = useNavigate();
  const { id } = useParams();
  const { enqueueSnackbar } = useSnackbar();

  const token = localStorage.getItem("token");

  const config = {
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    },
  };

  useEffect(() => {
    setLoading(true);

    const loadData = async () => {
      try {
        const [atletasRes, userRes] = await Promise.all([
          axios.get(`${import.meta.env.VITE_REACT_APP_BACKEND_BASEURL}/atletas`, config),
          axios.get(`${import.meta.env.VITE_REACT_APP_BACKEND_BASEURL}/atletas/${id}`, config),
        ]);

        const allAtletas = atletasRes.data?.data || [];
        setAtletasList(allAtletas);

        const userData = userRes.data;
        setNome(userData.nome || "");
        setEmail(userData.email || "");
        setPassword(userData.password || "");
        setChaveAcesso(userData.chaveAcesso || "");
        setCelular(userData.celular || "");
        setPerfil(userData.perfil || "");

        // Preenche atletas representados
        if (Array.isArray(userData.atletasRepresentados)) {
          const preSelected = userData.atletasRepresentados.map((item) => {
            if (typeof item === "object" && item !== null && item._id) {
              return { _id: item._id, nome: item.nome };
            }
            const found = allAtletas.find((a) => a._id === item);
            return found ? { _id: found._id, nome: found.nome } : { _id: item, nome: "Atleta" };
          });
          setSelectedRepresentados(preSelected);
        }

        // Preenche fotos de referência
        setFotosReferencia(userData.fotosReferencia || []);

        setLoading(false);
      } catch (error) {
        setLoading(false);
        console.error(error);
        enqueueSnackbar("Erro ao consultar dados do atleta.", { variant: "error" });
      }
    };

    loadData();
  }, [id]);

  const handleToggleAtleta = (atleta) => {
    setSelectedRepresentados((prev) => {
      const exists = prev.some((a) => a._id === atleta._id);
      if (exists) {
        return prev.filter((a) => a._id !== atleta._id);
      } else {
        return [...prev, { _id: atleta._id, nome: atleta.nome }];
      }
    });
  };

  const handleEditAtleta = () => {
    const data = {
      nome,
      email,
      password,
      celular,
      perfil,
      atletasRepresentados: selectedRepresentados.map((a) => a._id),
    };
    setLoading(true);
    axios
      .put(
        `${import.meta.env.VITE_REACT_APP_BACKEND_BASEURL}/atletas/${id}`,
        data,
        config
      )
      .then(() => {
        setLoading(false);
        enqueueSnackbar("Dados do usuário atualizados.", { variant: "success" });
        navigate("/admin/atletas");
      })
      .catch((error) => {
        setLoading(false);
        if (error.response && error.response.status === 401) {
          enqueueSnackbar("Sessão expirada. Faça login novamente.", {
            variant: "warning",
          });
          navigate("/admin");
        } else {
          enqueueSnackbar("Não foi possível atualizar", { variant: "error" });
        }
        console.log(error);
      });
  };

  const [generatingCode, setGeneratingCode] = useState(false);

  const handleGerarCodigoAleatorio = async () => {
    setGeneratingCode(true);
    try {
      const response = await axios.get(
        `${import.meta.env.VITE_REACT_APP_BACKEND_BASEURL}/atletas/gerar-codigo`,
        config
      );
      if (response.data?.code) {
        setPassword(response.data.code);
        enqueueSnackbar(`Novo código único gerado: ${response.data.code}`, {
          variant: "success",
        });
      }
    } catch (error) {
      console.error("Erro ao gerar código:", error);
      enqueueSnackbar("Erro ao gerar código aleatório", { variant: "error" });
    } finally {
      setGeneratingCode(false);
    }
  };

  const copyToClipboard = async (text) => {
    if (!text) return false;
    if (navigator.clipboard && window.isSecureContext) {
      try {
        await navigator.clipboard.writeText(text);
        return true;
      } catch (_) {}
    }
    try {
      const textArea = document.createElement("textarea");
      textArea.value = text;
      textArea.style.position = "fixed";
      textArea.style.top = "0";
      textArea.style.left = "0";
      textArea.style.width = "2em";
      textArea.style.height = "2em";
      textArea.style.background = "transparent";
      document.body.appendChild(textArea);
      textArea.focus();
      textArea.select();
      const successful = document.execCommand("copy");
      document.body.removeChild(textArea);
      return successful;
    } catch (err) {
      console.error("Falha ao copiar:", err);
      return false;
    }
  };

  const handleCopyLink = async () => {
    const key = chaveAcesso || password;
    if (!key) {
      enqueueSnackbar("Atleta sem chave ou senha cadastrada", { variant: "warning" });
      return;
    }
    const url = `${window.location.origin}/#/acesso?k=${key}`;
    const success = await copyToClipboard(url);
    if (success) {
      enqueueSnackbar("Link de acesso direto com chave segura copiado!", { variant: "success" });
    } else {
      enqueueSnackbar("Não foi possível copiar automaticamente.", { variant: "warning" });
    }
  };

  const handleCelularChange = (e) => {
    const numericValue = e.target.value.replace(/\D/g, ""); // Remove caracteres não numéricos
    setCelular(numericValue);
  };

  const handleFaceFileChange = async (e) => {
    const selectedFiles = Array.from(e.target.files || []);
    if (selectedFiles.length === 0) return;

    setUploadingFace(true);

    try {
      for (let i = 0; i < selectedFiles.length; i++) {
        const file = selectedFiles[i];
        setFaceStatusText(`Analisando rosto na foto ${i + 1}/${selectedFiles.length}...`);

        // 1. Processa no browser via face-api
        const faceResult = await extractReferenceFaceDescriptor(file);
        if (!faceResult.success) {
          enqueueSnackbar(`Foto ${file.name}: ${faceResult.error}`, {
            variant: "warning",
            autoHideDuration: 6000,
          });
          continue;
        }

        // 2. Faz upload da imagem limpa para fotos de referência
        setFaceStatusText(`Enviando foto ${i + 1}/${selectedFiles.length}...`);
        const uploadData = new FormData();
        uploadData.append("file", file);

        const uploadRes = await axios.post(
          `${import.meta.env.VITE_REACT_APP_BACKEND_BASEURL}/upload-rosto`,
          uploadData,
          {
            headers: {
              "Content-Type": "multipart/form-data",
              Authorization: `Bearer ${token}`,
            },
          }
        );

        const imageUrl = uploadRes.data.url;

        // 3. Salva a foto e o descritor biométrico no atleta
        setFaceStatusText(`Salvando biometria facial ${i + 1}/${selectedFiles.length}...`);
        const saveRes = await axios.post(
          `${import.meta.env.VITE_REACT_APP_BACKEND_BASEURL}/atletas/${id}/fotos-referencia`,
          {
            url: imageUrl,
            descriptor: faceResult.descriptor,
          },
          config
        );

        setFotosReferencia(saveRes.data.fotosReferencia || []);
        enqueueSnackbar(`Rosto de ${file.name} cadastrado com sucesso!`, {
          variant: "success",
        });
      }
    } catch (error) {
      console.error("Erro ao cadastrar foto de rosto:", error);
      enqueueSnackbar(
        "Erro ao processar foto de referência: " +
          (error.response?.data?.message || error.message),
        { variant: "error" }
      );
    } finally {
      setUploadingFace(false);
      setFaceStatusText("");
      if (faceInputRef.current) {
        faceInputRef.current.value = "";
      }
    }
  };

  const handleDeleteFacePhoto = async (fotoId) => {
    if (!window.confirm("Deseja remover esta foto de referência facial?")) {
      return;
    }

    try {
      setUploadingFace(true);
      const res = await axios.delete(
        `${import.meta.env.VITE_REACT_APP_BACKEND_BASEURL}/atletas/${id}/fotos-referencia/${fotoId}`,
        config
      );
      setFotosReferencia(res.data.fotosReferencia || []);
      enqueueSnackbar("Foto de referência removida.", { variant: "info" });
    } catch (error) {
      console.error("Erro ao remover foto:", error);
      enqueueSnackbar("Não foi possível remover a foto.", { variant: "error" });
    } finally {
      setUploadingFace(false);
    }
  };

  const filteredAtletas = atletasList
    .filter((a) => a._id !== id) // Não precisa selecionar a si próprio
    .filter((a) => a.nome?.toLowerCase().includes(searchAtletaTerm.toLowerCase()));

  return (
    <div className="p-6 bg-base-100 flex justify-center items-center mt-16">
      {loading && <Spinner />}
      <div className="container max-w-lg shadow-lg rounded-lg p-5 bg-base-100">
        <h1 className="text-3xl font-semibold my-4">Editar usuário</h1>
        <div className="my-4">
          <label htmlFor="nome" className="block text-md  mb-2">
            Nome*
          </label>
          <input
            id="nome"
            type="text"
            value={nome}
            onChange={(e) => setNome(e.target.value)}
            className="border border-base-300 px-4 py-2 w-full rounded-md"
            required
          />
          <label htmlFor="celular" className="block text-md  mb-2 mt-4">
            Celular
          </label>
          <input
            id="celular"
            type="text"
            value={celular}
            onChange={handleCelularChange}
            className="border border-base-300 px-4 py-2 w-full rounded-md"
          />

          <div className="flex justify-between items-center mb-2 mt-4">
            <label htmlFor="password" className="block text-md font-medium">
              Código de Acesso / Senha*
            </label>
            <div className="flex gap-2">
              <button
                type="button"
                onClick={handleCopyLink}
                className="btn btn-xs btn-outline btn-primary gap-1"
                title="Copiar link de acesso direto"
              >
                <FaCopy />
                <span>Copiar Link</span>
              </button>
              <button
                type="button"
                onClick={handleGerarCodigoAleatorio}
                disabled={generatingCode}
                className="btn btn-xs btn-outline btn-secondary gap-1"
                title="Gerar código aleatório único"
              >
                {generatingCode ? (
                  <span className="loading loading-spinner loading-xs"></span>
                ) : (
                  <FaRandom />
                )}
                <span>Gerar aleatório</span>
              </button>
            </div>
          </div>
          <input
            id="password"
            type="text"
            value={password}
            onChange={(e) => setPassword(e.target.value.toUpperCase())}
            className="border border-base-300 px-4 py-2 w-full rounded-md font-mono font-bold"
            required
          />
          <span className="text-xs text-base-content/60 mt-1 block">
            Código alfanumérico único para o atleta acessar a loja ou link direto.
          </span>

          <label htmlFor="perfil" className="block text-lg mb-2 mt-4">
            Perfil*
          </label>
          <select
            id="perfil"
            value={perfil}
            onChange={(e) => setPerfil(e.target.value)}
            className="w-full border border-base-300 px-4 py-2 rounded-md"
            required
          >
            <option value="" disabled>
              Selecione o perfil
            </option>
            <option value="Atleta">Atleta</option>
            <option value="Assessoria">Assessoria</option>
          </select>

          {/* Seleção de atletas que este usuário representa */}
          <div className="mt-4 border border-base-300 rounded-md p-3 bg-base-200/40">
            <label className="block text-md font-medium mb-1">
              Atletas representados <span className="text-xs font-normal opacity-70">(opcional)</span>
            </label>
            <p className="text-xs text-info font-medium mb-1">
              ✓ Por padrão, cada atleta sempre vê todas as fotos marcadas para ela.
            </p>
            <p className="text-xs opacity-75 mb-2">
              Use este campo caso este usuário também represente outros atletas (ex: assessorias ou pais), podendo ver e comprar fotos deles também.
            </p>

            {selectedRepresentados.length > 0 && (
              <div className="flex flex-wrap gap-1.5 mb-2">
                {selectedRepresentados.map((a) => (
                  <span
                    key={a._id}
                    className="badge badge-primary gap-1 py-3 px-2 text-xs"
                  >
                    {a.nome}
                    <button
                      type="button"
                      onClick={() => handleToggleAtleta(a)}
                      className="hover:text-red-300 ml-1 font-bold"
                      title="Remover"
                    >
                      ✕
                    </button>
                  </span>
                ))}
              </div>
            )}

            <input
              type="text"
              placeholder="Buscar atleta para adicionar..."
              value={searchAtletaTerm}
              onChange={(e) => setSearchAtletaTerm(e.target.value)}
              className="border border-base-300 px-3 py-1.5 w-full rounded-md text-sm mb-2 bg-base-100"
            />

            <div className="max-h-40 overflow-y-auto border border-base-300 rounded-md p-2 bg-base-100 divide-y divide-base-200">
              {filteredAtletas.length > 0 ? (
                filteredAtletas.map((atleta) => {
                  const isChecked = selectedRepresentados.some(
                    (a) => a._id === atleta._id
                  );
                  return (
                    <label
                      key={atleta._id}
                      className="flex items-center gap-2 py-1.5 px-1 hover:bg-base-200 rounded cursor-pointer text-sm"
                    >
                      <input
                        type="checkbox"
                        className="checkbox checkbox-sm checkbox-primary"
                        checked={isChecked}
                        onChange={() => handleToggleAtleta(atleta)}
                      />
                      <span className="font-medium">{atleta.nome}</span>
                      {atleta.perfil && (
                        <span className="text-[11px] opacity-60 ml-auto">
                          {atleta.perfil}
                        </span>
                      )}
                    </label>
                  );
                })
              ) : (
                <div className="text-xs text-center py-2 opacity-60">
                  Nenhum atleta encontrado
                </div>
              )}
            </div>
          </div>

          {/* Seção de Reconhecimento Facial */}
          <div className="mt-5 border border-primary/30 bg-primary/5 rounded-lg p-4">
            <div className="flex items-center justify-between mb-1">
              <label className="block text-md font-bold text-primary">
                📸 Fotos de Rosto para IA (Reconhecimento Facial)
              </label>
              <span className="badge badge-sm badge-primary font-semibold">
                {fotosReferencia.length} {fotosReferencia.length === 1 ? "foto" : "fotos"}
              </span>
            </div>
            <p className="text-xs opacity-75 mb-3 leading-relaxed">
              Adicione fotos nítidas do rosto deste atleta. Você pode adicionar quantas fotos quiser (diferentes ângulos, iluminação ou expressões) para aumentar a assertividade do sistema durante o upload de fotos. O processamento é 100% no seu navegador, sem custo.
            </p>

            {/* Galeria de fotos de rosto */}
            {fotosReferencia.length > 0 ? (
              <div className="grid grid-cols-3 sm:grid-cols-4 gap-2 mb-3">
                {fotosReferencia.map((foto, idx) => (
                  <div
                    key={foto._id || idx}
                    className="relative group rounded-lg overflow-hidden border border-base-300 shadow-sm aspect-square bg-base-200"
                  >
                    <img
                      src={foto.url}
                      alt={`Rosto ${idx + 1}`}
                      className="w-full h-full object-cover"
                    />
                    <button
                      type="button"
                      onClick={() => handleDeleteFacePhoto(foto._id)}
                      className="absolute top-1 right-1 bg-red-600 hover:bg-red-700 text-white rounded-full w-6 h-6 flex items-center justify-center text-xs opacity-90 group-hover:opacity-100 transition-opacity shadow"
                      title="Excluir foto"
                    >
                      ✕
                    </button>
                    <div className="absolute bottom-0 inset-x-0 bg-black/60 text-[10px] text-white text-center py-0.5">
                      Rosto {idx + 1}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-xs text-center py-3 bg-base-100/70 rounded-md border border-dashed border-base-300 mb-3 opacity-70">
                Nenhuma foto de rosto cadastrada para este atleta.
              </div>
            )}

            {uploadingFace && (
              <div className="flex items-center gap-2 text-xs text-primary font-semibold py-2">
                <span className="loading loading-spinner loading-xs"></span>
                <span>{faceStatusText || "Processando reconhecimento facial no navegador..."}</span>
              </div>
            )}

            <input
              type="file"
              accept="image/*"
              ref={faceInputRef}
              onChange={handleFaceFileChange}
              className="hidden"
              multiple
            />

            <button
              type="button"
              disabled={uploadingFace}
              onClick={() => faceInputRef.current?.click()}
              className="btn btn-outline btn-sm btn-primary w-full"
            >
              + Adicionar Foto(s) de Rosto
            </button>
          </div>

          <label htmlFor="email" className="block text-md  mb-2 mt-4">
            E-mail
          </label>

          <input
            id="email"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="border border-base-300 px-4 py-2 w-full rounded-md"
          />
          <button
            onClick={handleEditAtleta}
            className="w-full bg-green-500
                              hover:bg-green-800 text-white py-2 px-4 rounded-md mt-4"
          >
            Salvar
          </button>
          <Link to="/admin/atletas" className="btn mt-5">
            Voltar
          </Link>
        </div>
      </div>
    </div>
  );
};

export default EditAtleta;
